import { useQuery } from '@tanstack/react-query';
import { fetchGeneralSettings } from './fetcher';
import { useEffect, useState } from 'react';
import { GeneralSettingType } from '../types/GeneralSettingType';
import { DefaultUseQueryConfig } from 'Config';
import { GeneralSettingThemeType } from '../types/GeneralSettingThemeType';
import useLocalStorage from '../../../../context/useLocalStorage';
import ThemeInitialState from '../../../../layout/mantis/theme/ThemeInitialState';
import { CompanySetInitialState } from './CompanySetInitialState';

export default function useGetGeneralSettings() {
	const { isError, isSuccess, isLoading, data, error } = useQuery(['generalSettings'], fetchGeneralSettings, DefaultUseQueryConfig);

	const companyInitialSettings = CompanySetInitialState();

	const [companyName, setCompanyName] = useState<GeneralSettingType>(
		companyInitialSettings[0] ?? {
			key: 'company_address',
			value: 'Company Address'
		}
	);
	const [companyAddress, setCompanyAddress] = useState<GeneralSettingType>(
		companyInitialSettings[1] ?? {
			key: 'company_address',
			value: 'Company Address'
		}
	);
	const [companyLogo, setCompanyLogo] = useState<GeneralSettingType>(
		companyInitialSettings[2] ?? {
			key: 'company_logo',
			value: 'Company Logo'
		}
	);

	const [activeCompany, setActiveCompany] = useState<GeneralSettingType>(
		companyInitialSettings[3] ?? {
			key: 'active_company',
			value: null
		}
	);

	const [privacyPolicyEn, setPrivacyPolicyEn] = useState<GeneralSettingType>(
		companyInitialSettings[4] ?? {
			key: 'privacy_policy_en',
			value: ''
		}
	);

	const [privacyPolicyAr, setPrivacyPolicyAr] = useState<GeneralSettingType>(
		companyInitialSettings[5] ?? {
			key: 'privacy_policy_ar',
			value: ''
		}
	);

	const [termsServiceEn, setTermsServiceEn] = useState<GeneralSettingType>(
		companyInitialSettings[6] ?? {
			key: 'terms_service_en',
			value: ''
		}
	);

	const [termsServiceAr, setTermsServiceAr] = useState<GeneralSettingType>(
		companyInitialSettings[7] ?? {
			key: 'terms_service_ar',
			value: ''
		}
	);

	const [contactEmail, setContactEmail] = useState<GeneralSettingType>(
		companyInitialSettings[8] ?? {
			key: 'contact_email',
			value: ''
		}
	);

	const [contactPhoneNumber, setContactPhoneNumber] = useState<GeneralSettingType>(
		companyInitialSettings[9] ?? {
			key: 'contact_phone_number',
			value: ''
		}
	);

	const [dashboardTitle, setdashboardTitle] = useState<GeneralSettingType>(
		companyInitialSettings[10] ?? {
			key: 'dashboard_title',
			value: ''
		}
	);

	const [dashboardSubTitle, setdashboardSubTitle] = useState<GeneralSettingType>(
		companyInitialSettings[11] ?? {
			key: 'dashboard_sub_title',
			value: ''
		}
	);

	const [dashboardImage, setdashboardImage] = useState<GeneralSettingType>(
		companyInitialSettings[12] ?? {
			key: 'dashboard_image',
			value: ''
		}
	);

	const [registerWelcomeEn, setRegisterWelcomeEn] = useState<GeneralSettingType>(
		companyInitialSettings[13] ?? {
			key: 'register_welcome_en',
			value: ''
		}
	);

	const [registerWelcomeAr, setRegisterWelcomeAr] = useState<GeneralSettingType>(
		companyInitialSettings[14] ?? {
			key: 'register_welcome_ar',
			value: ''
		}
	);
	const [qrScanGuidelinesEn, setQrScanGuidelinesEn] = useState<GeneralSettingType>(
		companyInitialSettings[15] ?? {
			key: 'qr_scan_guidelines_en',
			value: ''
		}
	);
	const [qrScanGuidelinesAr, setQrScanGuidelinesAr] = useState<GeneralSettingType>(
		companyInitialSettings[16] ?? {
			key: 'qr_scan_guidelines_ar',
			value: ''
		}
	);

	const [afterRegisterPageSectionEn, setAfterRegisterPageSectionEn] = useState<GeneralSettingType>(
		companyInitialSettings[17] ?? {
			key: 'after_register_page_section_en',
			value: ''
		}
	);

	const [afterRegisterPageSectionAr, setAfterRegisterPageSectionAr] = useState<GeneralSettingType>(
		companyInitialSettings[18] ?? {
			key: 'after_register_page_section_ar',
			value: ''
		}
	);

	const [savedTheme] = useLocalStorage('dfnt-config', ThemeInitialState);

	const [companyTheme, setCompanyTheme] = useState<GeneralSettingThemeType>({
		key: 'general_theme',
		value: {
			general_theme: savedTheme
		}
	});

	useEffect(() => {
		console.error(error);
	}, [isError]);

	useEffect(() => {
		if (isSuccess) {
			if ('data' in data) {
				if ('data' in data.data) {
					let items = data.data.data;

					let newCompanySettings: Array<GeneralSettingType> = [
						{
							key: 'company_name',
							value: 'Company Name'
						},
						{
							key: 'company_address',
							value: 'Company Address'
						},
						{
							key: 'company_logo',
							value: 'Company Logo'
						},
						{
							key: 'active_company',
							value: null
						},
						{
							key: 'privacy_policy_en',
							value: ''
						},
						{
							key: 'privacy_policy_ar',
							value: ''
						},
						{
							key: 'terms_service_en',
							value: ''
						},
						{
							key: 'terms_service_ar',
							value: ''
						},
						{
							key: 'contact_email',
							value: ''
						},
						{
							key: 'contact_phone_number',
							value: ''
						},
						{
							key: 'dashboard_title',
							value: ''
						},
						{
							key: 'dashboard_sub_title',
							value: ''
						},
						{
							key: 'dashboard_image',
							value: ''
						},
						{
							key: 'register_welcome_en',
							value: ''
						},
						{
							key: 'register_welcome_ar',
							value: ''
						},
						{
							key: 'qr_scan_guidelines_en',
							value: ''
						},
						{
							key: 'qr_scan_guidelines_ar',
							value: ''
						},
						{
							key: 'after_register_page_section_en',
							value: ''
						},
						{
							key: 'after_register_page_section_ar',
							value: ''
						}
					];

					if ('company_name' in items) {
						setCompanyName({
							key: 'company_name',
							value: items.company_name ?? 'Company Name'
						});
						newCompanySettings[0].value = items.company_name ?? 'Company Name';
					}

					if ('company_address' in items) {
						setCompanyAddress({
							key: 'company_address',
							value: items.company_address ?? 'Company Address'
						});
						newCompanySettings[1].value = items.company_address ?? 'Company Address';
					}

					if ('company_logo' in items) {
						setCompanyLogo({
							key: 'company_logo',
							value: items.company_logo ?? 'Company Logo'
						});
						newCompanySettings[2].value = items.company_logo ?? 'Company Logo';
					}

					if ('active_company' in items) {
						setActiveCompany({
							key: 'active_company',
							value: items.active_company ?? null
						});
						newCompanySettings[3].value = items.active_company ?? null;
					}

					if ('privacy_policy_en' in items) {
						setPrivacyPolicyEn({
							key: 'privacy_policy_en',
							value: items.privacy_policy_en ?? ''
						});
						newCompanySettings[4].value = items.privacy_policy_en ?? '';
					}

					if ('privacy_policy_ar' in items) {
						setPrivacyPolicyAr({
							key: 'privacy_policy_ar',
							value: items.privacy_policy_ar ?? ''
						});
						newCompanySettings[5].value = items.privacy_policy_ar ?? '';
					}

					if ('terms_service_en' in items) {
						setTermsServiceEn({
							key: 'terms_service_en',
							value: items.terms_service_en ?? ''
						});
						newCompanySettings[6].value = items.terms_service_en ?? '';
					}

					if ('terms_service_ar' in items) {
						setTermsServiceAr({
							key: 'terms_service_ar',
							value: items.terms_service_ar ?? ''
						});
						newCompanySettings[7].value = items.terms_service_ar ?? '';
					}

					if ('contact_email' in items) {
						setContactEmail({
							key: 'contact_email',
							value: items.contact_email ?? ''
						});
						newCompanySettings[8].value = items.contact_email ?? '';
					}

					if ('contact_phone_number' in items) {
						setContactPhoneNumber({
							key: 'contact_phone_number',
							value: items.contact_phone_number ?? ''
						});
						newCompanySettings[9].value = items.contact_phone_number ?? '';
					}

					if ('dashboard_title' in items) {
						setdashboardTitle({
							key: 'dashboard_title',
							value: items.dashboard_title ?? ''
						});
						newCompanySettings[10].value = items.dashboard_title ?? '';
					}

					if ('dashboard_sub_title' in items) {
						setdashboardSubTitle({
							key: 'dashboard_sub_title',
							value: items.dashboard_sub_title ?? ''
						});
						newCompanySettings[11].value = items.dashboard_sub_title ?? '';
					}

					if ('dashboard_image' in items) {
						setdashboardImage({
							key: 'dashboard_image',
							value: items.dashboard_image ?? ''
						});
						newCompanySettings[12].value = items.dashboard_image ?? '';
					}

					if ('register_welcome_en' in items) {
						setRegisterWelcomeEn({
							key: 'register_welcome_en',
							value: items.register_welcome_en ?? ''
						});
						newCompanySettings[13].value = items.register_welcome_en ?? '';
					}

					if ('register_welcome_ar' in items) {
						setRegisterWelcomeAr({
							key: 'register_welcome_ar',
							value: items.register_welcome_ar ?? ''
						});
						newCompanySettings[14].value = items.register_welcome_ar ?? '';
					}

					if ('qr_scan_guidelines_en' in items) {
						setQrScanGuidelinesEn({
							key: 'qr_scan_guidelines_en',
							value: items.qr_scan_guidelines_en ?? ''
						});
						newCompanySettings[15].value = items.qr_scan_guidelines_en ?? '';
					}

					if ('qr_scan_guidelines_ar' in items) {
						setQrScanGuidelinesAr({
							key: 'qr_scan_guidelines_ar',
							value: items.qr_scan_guidelines_ar ?? ''
						});
						newCompanySettings[16].value = items.qr_scan_guidelines_ar ?? '';
					}

					if ('after_register_page_section_en' in items) {
						setAfterRegisterPageSectionEn({
							key: 'after_register_page_section_en',
							value: items.after_register_page_section_en ?? ''
						});
						newCompanySettings[17].value = items.after_register_page_section_en ?? '';
					}

					if ('after_register_page_section_ar' in items) {
						setAfterRegisterPageSectionAr({
							key: 'after_register_page_section_ar',
							value: items.after_register_page_section_ar ?? ''
						});
						newCompanySettings[18].value = items.after_register_page_section_ar ?? '';
					}

					if ('general_theme' in items) {
						setCompanyTheme({
							key: 'general_theme',
							value: {
								general_theme: items.general_theme ?? savedTheme
							}
						});
					}

					localStorage.setItem('company_settings', JSON.stringify(newCompanySettings));
				}
			}
		}
	}, [isSuccess]);

	return {
		companyName,
		companyAddress,
		companyLogo,
		companyTheme,
		activeCompany,
		privacyPolicyEn,
		privacyPolicyAr,
		termsServiceEn,
		termsServiceAr,
		afterRegisterPageSectionEn,
		afterRegisterPageSectionAr,
		contactEmail,
		contactPhoneNumber,
		dashboardTitle,
		dashboardSubTitle,
		dashboardImage,
		registerWelcomeEn,
		registerWelcomeAr,
		qrScanGuidelinesEn,
		qrScanGuidelinesAr
	};
}
